/**
 * Client-side helper that opens WhatsApp with a FULL, unabridged copy
 * of an enquiry — never truncated. If the encoded URL would exceed the
 * safe browser limit, the message is split into numbered parts and each
 * part is opened as its own wa.me tab in sequence, so no content is lost.
 *
 * The server has already stored the complete message; this helper is
 * purely for the courtesy WhatsApp notification.
 */

const WHATSAPP_NUMBER = "447586890299";

// Chrome/Safari cap the URL around ~8k chars. We stay well under that
// so URL-encoded payload has headroom for non-ASCII expansion.
const SAFE_URL_LEN = 6500;

// Raw-text chunk size for splitting (accounts for numbering prefix + URL encoding).
const CHUNK_SIZE = 2500;

const TYPE_LABELS = {
  general: "General Enquiry",
  property_enquiry: "Property Enquiry",
  valuation: "Rental Valuation Request",
  service_request: "Service Request",
  maintenance: "Maintenance Request",
};

const buildFullMessage = ({
  name,
  phone,
  email,
  submission_type,
  source,
  subject,
  reference,
  body,
}) => {
  const lines = [
    `New Enquiry — Keystone Properties`,
    subject ? `Subject: ${subject}` : null,
    "",
    `Name: ${name || "Not provided"}`,
    `Email: ${email || "Not provided"}`,
    `Phone: ${phone || "Not provided"}`,
    submission_type ? `Type: ${TYPE_LABELS[submission_type] || submission_type}` : null,
    reference ? `Reference: ${reference}` : null,
    source ? `Source: ${source}` : null,
    "",
    "Message:",
    body && body.trim() ? body : "(no message provided)",
    "",
    "— Submitted via thekeystoneproperties.co.uk",
  ].filter((line) => line !== null);
  return lines.join("\n");
};

const buildUrl = (text) =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;

const chunkString = (str, size) => {
  const parts = [];
  for (let i = 0; i < str.length; i += size) {
    parts.push(str.slice(i, i + size));
  }
  return parts;
};

/**
 * Opens the WhatsApp share tab(s) for a submitted enquiry.
 *
 * @param {Object} payload — see buildFullMessage for accepted fields.
 * @returns {{ parts: number, dispatched: number, urls: string[] }}
 */
export function openWhatsAppEnquiry(payload) {
  const fullText = buildFullMessage(payload);
  const singleUrl = buildUrl(fullText);

  // Fits in a single message — the common path
  if (singleUrl.length <= SAFE_URL_LEN) {
    try {
      window.open(singleUrl, "_blank", "noopener,noreferrer");
      return { parts: 1, dispatched: 1, urls: [singleUrl] };
    } catch {
      return { parts: 1, dispatched: 0, urls: [singleUrl] };
    }
  }

  // Split into numbered parts so nothing is lost
  const chunks = chunkString(fullText, CHUNK_SIZE);
  const total = chunks.length;
  const urls = chunks.map((chunk, i) => {
    const numbered = `(Part ${i + 1}/${total})\n\n${chunk}`;
    return buildUrl(numbered);
  });

  // Open the first tab immediately (in-user-gesture, popup-blocker friendly).
  // Subsequent tabs are opened after short delays; some browsers may still
  // block them, in which case the returned urls[] can be used by the caller
  // to render "Send part N" fallback buttons in a toast.
  let dispatched = 0;
  urls.forEach((url, i) => {
    setTimeout(() => {
      try {
        const w = window.open(url, "_blank", "noopener,noreferrer");
        if (w) dispatched += 1;
      } catch {
        /* popup blocked — caller can offer manual fallback */
      }
    }, i * 1400);
  });

  return { parts: total, dispatched, urls };
}
